import { useEffect, useMemo, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { PatientAlert } from "services/types/patient-alerts.type";

import usePatient from "../../use-patient.hook";

type PatientAlertsAccordionProps = {
  offsetTop?: boolean;
};

const formatAlertsCount = (count: number) =>
  `${count} ${count === 1 ? "Alert" : "Alerts"}`;

export function PatientAlertsAccordion({
  offsetTop = false
}: PatientAlertsAccordionProps) {
  const [expanded, setExpanded] = useState(false);
  const {
    alerts,
    fetchMoreAlerts,
    hasMoreAlerts,
    isLoadingAlerts,
    patientId,
    setLoadAlerts
  } = usePatient();

  useEffect(() => {
    setLoadAlerts(true);
  }, [setLoadAlerts]);

  useEffect(() => {
    if (!isLoadingAlerts && hasMoreAlerts && alerts.length > 0) {
      fetchMoreAlerts();
    }
  }, [alerts.length, fetchMoreAlerts, hasMoreAlerts, isLoadingAlerts]);

  const activeAlerts = useMemo(
    () =>
      (alerts as PatientAlert[]).filter((alert) => !alert.discarded_at),
    [alerts]
  );

  const alertCount = activeAlerts.length;
  const bodyId = `patient-drawer-alerts-${patientId}`;

  if (alertCount === 0) {
    return null;
  }

  return (
    <Box sx={{ width: "100%", mt: offsetTop ? "25px" : 0 }}>
      <ButtonBase
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          backgroundColor: "#E23B3B",
          color: "white",
          px: 2,
          py: 1.25,
          textAlign: "left",
          border: 0,
          "&:hover": {
            backgroundColor: "#E23B3B"
          }
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: "100%" }}
        >
          <WarningAmberIcon sx={{ fontSize: 22, color: "inherit" }} />
          <Typography
            variant="body2"
            sx={{ color: "inherit", fontWeight: 500, flex: 1 }}
          >
            {formatAlertsCount(alertCount)}
          </Typography>
          <ExpandMoreIcon
            sx={{
              color: "inherit",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.18s ease"
            }}
          />
        </Stack>
      </ButtonBase>
      <Collapse in={expanded} timeout={180}>
        <Box id={bodyId} sx={{ backgroundColor: "#C42E2E", color: "white" }}>
          {activeAlerts.map((alert, index) => (
            <Stack
              key={alert.id}
              direction="row"
              spacing={1.25}
              sx={{
                px: 2,
                py: 1.25,
                borderBottom:
                  index < activeAlerts.length - 1
                    ? "0.5px solid rgba(255,255,255,0.22)"
                    : undefined
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "inherit", fontWeight: 500, lineHeight: 1.35 }}
              >
                {index + 1}.
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "inherit", fontWeight: 500, lineHeight: 1.35 }}
              >
                {alert.alert_description || "No description"}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
